import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ProductsArray } from '../types';
import { Store } from '../api/types';
import { productsApi, storeApi } from '../api';
import { isBackendEnabled } from '../api/client';

// Async thunk for loading products
export const loadProducts = createAsyncThunk(
  'app/loadProducts',
  async (): Promise<ProductsArray> => {
    try {
      // Use backend API if enabled
      if (isBackendEnabled()) {
        const response = await productsApi.getProducts();
        return response.products;
      }
      
      // Fallback to local data if backend is disabled
      const productsModule = await import('../assets/data/products.json');
      const { transformStoreDataArray } = await import('../utils/transformStoreData');
      return transformStoreDataArray(productsModule.default);
    } catch (error) {
      console.warn('Backend API failed, trying local data...', error);
      
      // Fallback: use local JSON file
      try {
        const productsModule = await import('../assets/data/products.json');
        const { transformStoreDataArray } = await import('../utils/transformStoreData');
        return transformStoreDataArray(productsModule.default);
      } catch (localError) {
        console.warn('Local import failed, trying alternative loading method...');
      const { loadProductsSafely } = await import('../utils/jsonLoader');
      return await loadProductsSafely();
      }
    }
  }
);

// Async thunk for loading stores
export const loadStores = createAsyncThunk(
  'app/loadStores',
  async (): Promise<Store[]> => {
    const response = await storeApi.getStores();
    return response.stores;
  }
);

interface AppState {
  products: ProductsArray;
  loading: boolean;
  error: string | null;
  productsLoaded: boolean;
  stores: Store[];
  storesLoaded: boolean;
  storesLoading: boolean;
  storesError: string | null;
}

const initialState: AppState = {
  products: [],
  loading: false,
  error: null,
  productsLoaded: false,
  stores: [],
  storesLoaded: false,
  storesLoading: false,
  storesError: null,
};

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    clearProducts: (state) => {
      state.products = [];
      state.productsLoaded = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadProducts.fulfilled, (state, action: PayloadAction<ProductsArray>) => {
        state.loading = false;
        state.products = action.payload;
        state.productsLoaded = true;
      })
      .addCase(loadProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load products';
      })
      .addCase(loadStores.pending, (state) => {
        state.storesLoading = true;
        state.storesError = null;
      })
      .addCase(loadStores.fulfilled, (state, action: PayloadAction<Store[]>) => {
        state.storesLoading = false;
        state.stores = action.payload;
        state.storesLoaded = true;
      })
      .addCase(loadStores.rejected, (state, action) => {
        state.storesLoading = false;
        state.storesError = action.error.message || 'Failed to load stores';
      });
  },
});

export const { clearProducts } = appSlice.actions;
export default appSlice.reducer;